import { useEffect, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { EkycUnderReviewBanner } from '@/features/ekyc/components'
import {
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { useSpProfile } from '@/features/rewards/api'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useNotify } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  readWebSurveyPhoneVerified,
  webSurveyMobileOnboardingPath,
} from '@/features/auth/lib/constants'
import { AvailableSurveyCard } from '../components/AvailableSurveyCard'
import { HomeMobilePromo } from '../components/HomeMobilePromo'
import { HomeStatsRow } from '../components/HomeStatsRow'
import { SurveysComingSoonEmptyState } from '../components/SurveysComingSoonEmptyState'
import { WebSurveyIneligibleBanner } from '../components/WebSurveyIneligibleBanner'
import { useAvailableSurveys } from '../api/available-surveys-queries'
import type { AvailableSurveyApiItem } from '../api/available-surveys-queries'
import {
  getSignupSurveyInstrumentId,
  isSignupSurveySubmitted,
  mergeHomeSurveys,
  useSignupSurveyEligibility,
} from '@/features/signup-survey'
import {
  homeContentInnerSx,
  homeContentShellSx,
  homeHeroGreetingSx,
  homeHeroWelcomeSx,
  providerShellHeroShellSx,
  providerShellHeroSx,
  homePageRootSx,
  homeSectionTitleSx,
} from '../styles/homeStyles'
import { shouldHideSdSurveysForRegistrationType } from '../lib/webSurveyIneligibleBanner'

/** Returns the first name from a display name string, or null when unavailable. */
function greetingFirstName(displayName: string | undefined): string | null {
  const part = displayName?.trim().split(/\s+/)[0]
  return part?.length ? part : null
}

/** Converts decimal minutes into "Est. X mins Y secs" (e.g. 5.5 → "Est. 5 mins 30 secs"). */
function formatDuration(minutes: number): string {
  const totalSeconds = Math.round(minutes * 60)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  if (mins === 0) return `Est. ${secs} secs`
  if (secs === 0) return `Est. ${mins} mins`
  return `Est. ${mins} mins ${secs} secs`
}

// ─── Sub-components (extracted to avoid nested ternaries) ───────────────────

interface HeroGreetingProps {
  firstName: string | null
}

function HeroGreeting({ firstName }: Readonly<HeroGreetingProps>) {
  if (firstName == null) {
    return (
      <Typography component="h1" sx={homeHeroGreetingSx} data-testid="home-greeting">
        Hey! Welcome to Brushfire.
      </Typography>
    )
  }
  return (
    <>
      <Typography
        component="h1"
        sx={{
          ...homeHeroGreetingSx,
          maxWidth: '100%',
          overflow: { xs: 'visible', md: 'hidden' },
          whiteSpace: { xs: 'normal', md: 'nowrap' },
          textOverflow: { xs: 'clip', md: 'ellipsis' },
        }}
        data-testid="home-greeting"
      >
        {`Hey, ${firstName}!`}
      </Typography>
      <Typography sx={homeHeroWelcomeSx}>Welcome to Brushfire</Typography>
    </>
  )
}

interface SurveySectionProps {
  isLoading: boolean
  isError: boolean
  surveys: AvailableSurveyApiItem[]
  surveyGridSx: object
  onRetry: () => void
  underReview: boolean
  onUnderReviewBlocked: () => void
}

function SurveySectionSkeleton({ surveyGridSx }: Readonly<{ surveyGridSx: object }>) {
  return (
    <Box sx={surveyGridSx} data-testid="home-surveys-skeleton">
      {[1, 2].map((n) => (
        <Box
          key={n}
          sx={{
            bgcolor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
          aria-busy="true"
          aria-label="Loading survey"
        >
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="text" width="75%" height={20} />
          <Box sx={{ display: 'flex', gap: 1.5, my: 0.5 }}>
            <Skeleton variant="rounded" width={120} height={35} sx={{ borderRadius: '30px' }} />
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: '30px' }} />
          </Box>
          <Skeleton variant="rounded" width="100%" height={45} sx={{ borderRadius: '8px' }} />
        </Box>
      ))}
    </Box>
  )
}

function SurveySectionError({ onRetry }: Readonly<{ onRetry: () => void }>) {
  return (
    <Box sx={{ textAlign: 'center', py: 6 }} data-testid="home-surveys-error" role="status">
      <Typography sx={{ color: '#4A5565', fontSize: '16px', lineHeight: '24px', mb: 2 }}>
        Unable to load dashboard. Please try again.
      </Typography>
      <Button
        variant="outlined"
        onClick={onRetry}
        data-testid="home-surveys-retry"
        sx={{ textTransform: 'none', borderRadius: '8px' }}
      >
        Retry
      </Button>
    </Box>
  )
}

function SurveyGrid({
  surveys,
  surveyGridSx,
  underReview,
  onUnderReviewBlocked,
}: Readonly<{
  surveys: AvailableSurveyApiItem[]
  surveyGridSx: object
  underReview: boolean
  onUnderReviewBlocked: () => void
}>) {
  return (
    <Box sx={surveyGridSx}>
      {surveys.map((survey) => (
        <AvailableSurveyCard
          key={survey.instrumentId}
          survey={{
            id: survey.instrumentId,
            title: survey.name,
            description: survey.description?.trim() ?? '',
            estimatedDuration: formatDuration(survey.estimatedCompletionMinutes),
            rewardEnabled: survey.reward.enabled,
            rewardAmount: survey.reward.display ?? '$0.00',
          }}
          activeResponseId={survey.hasActiveSession ? survey.activeResponseId : null}
          underReview={underReview}
          onUnderReviewBlocked={onUnderReviewBlocked}
        />
      ))}
    </Box>
  )
}

function SurveySection({
  isLoading,
  isError,
  surveys,
  surveyGridSx,
  onRetry,
  underReview,
  onUnderReviewBlocked,
}: Readonly<SurveySectionProps>) {
  if (isLoading) {
    return <SurveySectionSkeleton surveyGridSx={surveyGridSx} />
  }
  if (isError) {
    return <SurveySectionError onRetry={onRetry} />
  }
  if (surveys.length === 0) {
    return <SurveysComingSoonEmptyState />
  }
  return (
    <SurveyGrid
      surveys={surveys}
      surveyGridSx={surveyGridSx}
      underReview={underReview}
      onUnderReviewBlocked={onUnderReviewBlocked}
    />
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function HomePage() {
  useDocumentTitle('Home')
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useNotify()
  const userName = useSessionStore((s) => s.user?.name)
  const isGuest = useSessionStore((s) => s.user?.guest === true)
  const firstName = greetingFirstName(userName)
  const surveysQuery = useAvailableSurveys()
  const loginToastShown = useRef(false)

  // Login success toast (CT-01) — shown once when navigating from LoginPage
  useEffect(() => {
    const routeState = (location.state as Record<string, unknown> | null) ?? {}
    if (routeState.loginSuccess === true && !loginToastShown.current) {
      loginToastShown.current = true
      showToast('Signed in successfully', 'success', 3000)
      // Clear state so toast won't reappear on back-navigation
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, showToast])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('focus') !== 'mobile-app') return
    const promo = document.querySelector('[data-testid="home-mobile-promo"]')
    promo?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [location.search])

  const { data: profile } = useSpProfile()
  const underReview = isEkycUnderReview(profile?.ekycStatus)
  const userId = useSessionStore((s) => s.user?.id)
  const hideSdSurveys = shouldHideSdSurveysForRegistrationType(profile)
  const signupEligibilityQuery = useSignupSurveyEligibility()
  const signupSubmitted = Boolean(userId && isSignupSurveySubmitted(userId))
  const signupInstrumentId = userId ? getSignupSurveyInstrumentId(userId) : null
  const signupCard =
    hideSdSurveys || signupSubmitted ? null : (signupEligibilityQuery.data ?? null)
  // Until signup eligibility resolves, do not paint other available surveys (avoids flash).
  const awaitingSignupEligibility =
    Boolean(userId) && !signupSubmitted && !signupEligibilityQuery.isFetched && !hideSdSurveys
  const surveys = mergeHomeSurveys(
    hideSdSurveys || awaitingSignupEligibility ? [] : (surveysQuery.data ?? []),
    signupCard,
    signupSubmitted ? signupInstrumentId : null,
  )
  const surveyCount = surveys.length

  // Single survey → full-width; two or more → two-column (TC-SPW-DB-26/27)
  const surveyGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'minmax(0, 1fr)',
      md: surveyCount >= 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
    },
    gap: '32px',
    mb: 5,
  } as const

  const onUnderReviewBlocked = () => {
    showToast(EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
  }

  if (isGuest) {
    if (readWebSurveyPhoneVerified()) {
      return <Navigate to="/instrument/onboarding/account" replace />
    }
    return <Navigate to={webSurveyMobileOnboardingPath()} replace />
  }

  return (
    <Box sx={homePageRootSx} data-testid="provider-home-page">
      <Box sx={providerShellHeroSx}>
        <Box sx={providerShellHeroShellSx}>
          <Box sx={homeContentInnerSx}>
            <HeroGreeting firstName={firstName} />
          </Box>
        </Box>
      </Box>

      <HomeStatsRow />

      <Box sx={{ ...homeContentShellSx, flex: 1 }}>
        <Box sx={homeContentInnerSx}>
          <WebSurveyIneligibleBanner />
          <EkycUnderReviewBanner />

          {hideSdSurveys ? null : (
            <>
          <Typography component="h2" sx={homeSectionTitleSx} data-testid="home-surveys-heading">
            Available Surveys
          </Typography>

          <SurveySection
            isLoading={
              surveysQuery.isLoading ||
              // Wait for signup eligibility before showing empty — available list
              // excludes signup surveys, so finishing first would flash "coming soon".
              (Boolean(userId) &&
                !signupSubmitted &&
                !signupEligibilityQuery.isFetched &&
                (signupEligibilityQuery.isPending || signupEligibilityQuery.isFetching))
            }
            isError={surveysQuery.isError && surveys.length === 0}
            surveys={surveys}
            surveyGridSx={surveyGridSx}
            onRetry={() => {
              Promise.resolve(surveysQuery.refetch()).catch(() => undefined)
              Promise.resolve(signupEligibilityQuery.refetch()).catch(() => undefined)
            }}
            underReview={underReview}
            onUnderReviewBlocked={onUnderReviewBlocked}
          />
            </>
          )}

          <HomeMobilePromo />
        </Box>
      </Box>
    </Box>
  )
}
